// @ts-nocheck
import { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button, buttonVariants } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { QuickAddModal } from "@/components/QuickAddModal";
import {
  Clock,
  Briefcase,
  User,
  Plus
} from "lucide-react";
import { format, isSameDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useDrop } from 'react-dnd';
import { cn } from "@/lib/utils";

interface Appointment {
  id: string;
  title: string;
  appointment_date: string;
  duration: number;
  status: string;
  priority: string;
  appointment_type: string;
  color_tag: string;
  clients?: { name: string };
  cases?: { title: string };
  case_id?: string;
  prospect_name?: string;
}

interface Task {
  id: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  due_date?: string;
  assigned_to?: string;
  case_id?: string;
  created_at: string;
  cases?: { title: string };
}

interface AppointmentCalendarProps {
  appointments: Appointment[];
  tasks?: Task[];
  selectedDate?: Date;
  onDateSelect: (date: Date | undefined) => void;
  onAppointmentSelect: (appointment: Appointment) => void;
  onAppointmentDrop: (appointmentId: string, newDate: Date) => void;
  onCreateAppointment?: (date: Date) => void;
  onCreateTask?: (date: Date) => void;
}

const ItemTypes = {
  APPOINTMENT: 'appointment',
};

export const AppointmentCalendar = ({
  appointments,
  tasks = [],
  selectedDate,
  onDateSelect,
  onAppointmentSelect,
  onAppointmentDrop,
  onCreateAppointment,
  onCreateTask
}: AppointmentCalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [quickAddModalOpen, setQuickAddModalOpen] = useState(false);
  const [selectedDateForQuickAdd, setSelectedDateForQuickAdd] = useState<Date | null>(null);

  const getAppointmentsForDate = (date: Date) => {
    return appointments.filter(apt => {
      const aptDate = new Date(apt.appointment_date);
      const aptDateLocal = new Date(aptDate.getFullYear(), aptDate.getMonth(), aptDate.getDate());
      const targetDateLocal = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      return aptDateLocal.getTime() === targetDateLocal.getTime();
    }).sort((a, b) => new Date(a.appointment_date).getTime() - new Date(b.appointment_date).getTime());
  };

  const getTasksForDate = (date: Date) => {
    return tasks.filter(task => {
      if (!task.due_date) return false;
      
      const taskDate = new Date(task.due_date);
      const taskDateLocal = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());
      const targetDateLocal = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      return taskDateLocal.getTime() === targetDateLocal.getTime();
    }).sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime());
  };

  const DayContent = ({ date }: { date: Date }) => {
    const [{ isOver, canDrop }, drop] = useDrop(() => ({
      accept: ItemTypes.APPOINTMENT,
      drop: (item: { id: string }) => onAppointmentDrop(item.id, date),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }));

    const isActive = isOver && canDrop;
    const dayAppointments = getAppointmentsForDate(date);
    const dayTasks = getTasksForDate(date);
    // Até 5 itens por dia: compromissos primeiro, tarefas ocupam o restante
    const MAX_ITEMS = 5;
    const shownAppointments = dayAppointments.slice(0, MAX_ITEMS);
    const shownTasks = dayTasks.slice(0, Math.max(0, MAX_ITEMS - shownAppointments.length));
    const hiddenCount = dayAppointments.length + dayTasks.length - shownAppointments.length - shownTasks.length;

    return (
      <div
        ref={drop}
        className={cn(
          `relative flex flex-col h-full w-full p-2 overflow-hidden group`,
          `border-t border-border/50 transition-colors`,
          isActive ? 'bg-white/10' : 'hover:bg-white/10'
        )}
        onClick={() => onDateSelect(date)}
      >
        <div className="flex items-center justify-between mb-1">
          {isSameDay(date, new Date()) ? (
            // Hoje: número dentro de uma bolinha dourada
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#c4ab83] text-sm font-semibold text-white shadow-[0_2px_8px_-2px_rgba(154,127,82,0.6)]" title="Hoje">
              {date.getDate()}
            </span>
          ) : (
            <span className="font-semibold">{date.getDate()}</span>
          )}
          {/* Botão discreto de + que aparece no hover */}
          {(onCreateAppointment || onCreateTask) && (
            <div
              className={cn(
                buttonVariants({ variant: "ghost", size: "sm" }),
                "opacity-0 group-hover:opacity-100 transition-opacity duration-200 h-6 w-6 p-0 hover:bg-white/20 cursor-pointer"
              )}
              onClick={(e) => {
                e.stopPropagation();
                // Se ambos os callbacks existem, mostrar modal de escolha
                if (onCreateAppointment && onCreateTask) {
                  setSelectedDateForQuickAdd(date);
                  setQuickAddModalOpen(true);
                } else if (onCreateAppointment) {
                  onCreateAppointment(date);
                } else if (onCreateTask) {
                  onCreateTask(date);
                }
              }}
              role="button"
            >
              <Plus className="h-3 w-3" />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 w-full flex-1 overflow-y-auto">
          {/* Compromissos: horário em destaque; detalhes ao passar o mouse */}
          {shownAppointments.map((apt) => {
            const start = new Date(apt.appointment_date);
            const end = new Date(start.getTime() + (Number(apt.duration) || 60) * 60000);
            const done = apt.status === "Concluído";
            const color = apt.color_tag || "#c4ab83";
            const person = apt.clients?.name || apt.prospect_name;
            return (
              <HoverCard key={apt.id} openDelay={200} closeDelay={400}>
                <HoverCardTrigger asChild>
                  <div
                    className={cn(
                      "flex w-full cursor-pointer items-center gap-1.5 overflow-hidden rounded-md border py-0.5 pl-1 pr-1.5 text-left text-[11px] shadow-[0_1px_2px_rgba(15,23,42,0.06)] transition-all hover:brightness-95",
                      done && "opacity-50"
                    )}
                    style={{ backgroundColor: color + "24", borderColor: color + "66" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAppointmentSelect(apt);
                    }}
                  >
                    <span className="h-4 w-[3px] shrink-0 rounded-full" style={{ backgroundColor: color }} />
                    <span className="shrink-0 font-semibold tabular-nums text-slate-900">{format(start, "HH:mm")}</span>
                    <span className={cn("truncate font-medium text-slate-700", done && "line-through")}>{apt.title}</span>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent align="start" className="w-64 rounded-xl border-[#c4ab83]/30 p-3">
                  <div className="flex items-start gap-2.5">
                    <span className="mt-1 h-8 w-1 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900">{apt.title}</p>
                      <p className="text-xs tabular-nums text-slate-500">
                        {format(start, "HH:mm")} – {format(end, "HH:mm")} · {format(start, "EEE, d MMM", { locale: ptBR })}
                      </p>
                    </div>
                  </div>
                  {(person || apt.cases) && (
                    <div className="mt-2.5 space-y-1 border-t border-slate-100 pt-2.5 text-xs">
                      {person && (
                        <p className="flex items-center gap-1.5 text-slate-700">
                          <User className="h-3.5 w-3.5 shrink-0 text-[#c4ab83]" />
                          <span className="truncate">{person}</span>
                        </p>
                      )}
                      {apt.cases && (
                        <a
                          href={`/processo/${apt.case_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1.5 text-[#7a6440] hover:underline"
                          title={apt.cases.title}
                        >
                          <Briefcase className="h-3.5 w-3.5 shrink-0 text-[#c4ab83]" />
                          <span className="truncate">{apt.cases.title}</span>
                        </a>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    className="mt-2.5 w-full rounded-lg bg-slate-900 py-1.5 text-xs font-medium text-white transition-colors hover:bg-slate-800"
                    onClick={(e) => { e.stopPropagation(); onAppointmentSelect(apt); }}
                  >
                    Ver detalhes
                  </button>
                </HoverCardContent>
              </HoverCard>
            );
          })}

          {/* Tarefas */}
          {shownTasks.map((task) => {
            const dueDate = task.due_date ? new Date(task.due_date) : null;
            const taskDone = /conclu/i.test(String(task.status || ''));
            const isOverdue = dueDate && dueDate < new Date() && !taskDone;
            const hasTime = dueDate && (dueDate.getHours() !== 0 || dueDate.getMinutes() !== 0);
            return (
              <HoverCard key={task.id} openDelay={200} closeDelay={400}>
                <HoverCardTrigger asChild>
                  <div
                    className={cn(
                      "flex w-full cursor-pointer items-center gap-1.5 overflow-hidden rounded-md border border-dashed py-0.5 pl-1 pr-1.5 text-left text-[11px] transition-colors",
                      taskDone
                        ? "border-emerald-200 bg-emerald-50/60 opacity-70 hover:opacity-100"
                        : isOverdue ? "border-red-300 bg-red-50 hover:bg-red-100/70" : "border-amber-300 bg-amber-50 hover:bg-amber-100/70"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.location.href = '/tasks';
                    }}
                  >
                    <span className={cn("h-4 w-[3px] shrink-0 rounded-full", taskDone ? "bg-emerald-500" : isOverdue ? "bg-red-500" : "bg-amber-500")} />
                    {taskDone && <span className="shrink-0 text-[10px] text-emerald-600" aria-label="Concluída">✓</span>}
                    {hasTime && <span className={cn("shrink-0 font-semibold tabular-nums", taskDone ? "text-emerald-700 line-through" : "text-slate-700")}>{format(dueDate!, "HH:mm")}</span>}
                    <span className={cn("truncate font-medium", taskDone ? "text-emerald-800 line-through decoration-emerald-600/70" : isOverdue ? "text-red-700" : "text-amber-800")}>{task.title}</span>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent align="start" className="w-64 rounded-xl border-[#c4ab83]/30 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#9a7f52]">Tarefa</p>
                  <p className="mt-0.5 text-sm font-semibold text-slate-900">{task.title}</p>
                  <p className={cn("text-xs", isOverdue ? "text-red-600" : "text-slate-500")}>
                    {dueDate ? `Prazo ${format(dueDate, hasTime ? "dd/MM 'às' HH:mm" : "dd/MM", { locale: ptBR })}` : 'Sem prazo'}
                    {taskDone ? ' · concluída ✓' : isOverdue ? ' · atrasada' : ''}
                  </p>
                  {task.cases && <p className="mt-1.5 truncate text-xs text-[#7a6440]">{task.cases.title}</p>}
                  <button
                    type="button"
                    className="mt-2.5 w-full rounded-lg border border-slate-200 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:border-[#c4ab83] hover:bg-[#faf7f2]"
                    onClick={(e) => { e.stopPropagation(); window.location.href = '/tasks'; }}
                  >
                    Abrir tarefas
                  </button>
                </HoverCardContent>
              </HoverCard>
            );
          })}

          {/* Contador de itens adicionais */}
          {hiddenCount > 0 && (
            <div className="mt-0.5 text-center text-[11px] text-slate-400">+{hiddenCount} mais</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <Calendar
        mode="single"
        selected={selectedDate}
        onSelect={onDateSelect}
        month={currentMonth}
        onMonthChange={setCurrentMonth}
        locale={ptBR}
        className="w-full h-full flex flex-col"
        classNames={{
          months: "flex flex-col flex-1",
          month: "space-y-4 flex flex-col flex-1",
          caption: "flex justify-center pt-1 relative items-center",
          caption_label: "text-lg font-medium",
          nav: "space-x-1 flex items-center",
          nav_button: "h-9 w-9 bg-transparent p-0 opacity-50 hover:opacity-100",
          table: "w-full border-collapse flex flex-col flex-1",
          head_row: "flex w-full justify-between",
          head_cell: "text-muted-foreground rounded-md w-full justify-center font-normal text-[0.8rem]",
          row: "flex w-full mt-2 justify-between flex-1 gap-2",
          cell: "aspect-square h-full w-full text-center text-sm p-0 relative focus-within:relative focus-within:z-20 bg-card/50 rounded-lg shadow-inner transition-colors",
          day: "h-full w-full p-0 font-normal aria-selected:opacity-100",
          day_selected: "bg-white/20 backdrop-blur-sm ring-2 ring-inset ring-white/30 text-foreground",
          day_today: "bg-white/10 ring-1 ring-inset ring-white/20 text-foreground",
          day_outside: "text-muted-foreground opacity-50",
          day_disabled: "text-muted-foreground opacity-50",
          day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
          day_hidden: "invisible",
        }}
        components={{
          DayContent: DayContent,
        }}
      />
      
      {/* Modal de escolha rápida */}
      {selectedDateForQuickAdd && (
        <QuickAddModal
          isOpen={quickAddModalOpen}
          onClose={() => {
            setQuickAddModalOpen(false);
            setSelectedDateForQuickAdd(null);
          }}
          selectedDate={selectedDateForQuickAdd}
          onCreateAppointment={onCreateAppointment!}
          onCreateTask={onCreateTask!}
        />
      )}
    </>
  );
};